import apiClient from './client';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Holiday {
  id?: string;
  date: string; // ISO date
  name: string;
  type?: string;
  countryCode?: string | null;
}

export interface HolidayCalendar {
  id: string;
  name: string;
  badge_color: string;
  description?: string | null;
  is_default: boolean;
  holidays: Holiday[];
  /** Monday user IDs (string) this calendar is assigned to */
  assignedUserIds: string[];
  created_at: string;
  updated_at: string;
}

// ─── Calendar CRUD ────────────────────────────────────────────────────────────

/** List all holiday calendars for the current account (includes holidays + assignments inline). */
export async function getHolidayCalendars(): Promise<HolidayCalendar[]> {
  const { data } = await apiClient.get<HolidayCalendar[]>(
    '/holiday-calendars',
  );
  return data;
}

/** Get a single holiday calendar by ID (includes holidays + assignments inline). */
export async function getHolidayCalendar(id: string): Promise<HolidayCalendar> {
  const { data } = await apiClient.get<HolidayCalendar>(
    `/holiday-calendars/${id}`,
  );
  return data;
}

/** Create a new holiday calendar (metadata only). */
export async function createHolidayCalendar(payload: {
  name: string;
  badgeColor?: string;
  description?: string | null;
  isDefault?: boolean;
}): Promise<HolidayCalendar> {
  const { data } = await apiClient.post<HolidayCalendar>(
    '/holiday-calendars',
    payload,
  );
  return data;
}

/** Update an existing holiday calendar (metadata only). */
export async function updateHolidayCalendar(
  id: string,
  payload: {
    name?: string;
    badgeColor?: string;
    description?: string | null;
    isDefault?: boolean;
  },
): Promise<HolidayCalendar> {
  const { data } = await apiClient.patch<HolidayCalendar>(
    `/holiday-calendars/${id}`,
    payload,
  );
  return data;
}

/** Delete a holiday calendar. */
export async function deleteHolidayCalendar(id: string): Promise<void> {
  await apiClient.delete(`/holiday-calendars/${id}`);
}

/** Export a holiday calendar as a CSV blob. */
export async function exportHolidayCalendar(id: string): Promise<Blob> {
  const { data } = await apiClient.get(`/holiday-calendars/${id}/export`, {
    responseType: 'blob',
  });
  return data as Blob;
}

// ─── Holiday CRUD ─────────────────────────────────────────────────────────────

/** Create a holiday within a calendar. */
export async function createHoliday(
  calendarId: string,
  payload: { name: string; date: string; type?: string; countryCode?: string | null },
): Promise<Holiday> {
  const { data } = await apiClient.post<Holiday>(
    `/holiday-calendars/${calendarId}/holidays`,
    payload,
  );
  return data;
}

/** Update a holiday within a calendar. */
export async function updateHoliday(
  calendarId: string,
  holidayId: string,
  payload: { name?: string; date?: string; type?: string; countryCode?: string | null },
): Promise<Holiday> {
  const { data } = await apiClient.patch<Holiday>(
    `/holiday-calendars/${calendarId}/holidays/${holidayId}`,
    payload,
  );
  return data;
}

/** Delete a holiday within a calendar. */
export async function deleteHolidayFromCalendar(
  calendarId: string,
  holidayId: string,
): Promise<void> {
  await apiClient.delete(
    `/holiday-calendars/${calendarId}/holidays/${holidayId}`,
  );
}

// ─── Assignment CRUD ──────────────────────────────────────────────────────────

/** Assign a user to a holiday calendar. */
export async function assignUserToCalendar(
  calendarId: string,
  mondayUserId: string,
): Promise<{ id: string; calendar_id: string; monday_user_id: string }> {
  const { data } = await apiClient.post(
    `/holiday-calendars/${calendarId}/assignments`,
    { mondayUserId },
  );
  return data;
}

/** Get all assignments for a calendar. */
export async function getCalendarAssignments(
  calendarId: string,
): Promise<Array<{ id: string; calendar_id: string; monday_user_id: string }>> {
  const { data } = await apiClient.get(
    `/holiday-calendars/${calendarId}/assignments`,
  );
  return data;
}

/** Remove a user assignment from a calendar. */
export async function removeCalendarAssignment(
  calendarId: string,
  assignmentId: string,
): Promise<void> {
  await apiClient.delete(
    `/holiday-calendars/${calendarId}/assignments/${assignmentId}`,
  );
}
